import { useEffect, useMemo, useState } from 'react';
import { Subtitle1 } from '@fluentui/react-components';
import { useParams } from 'react-router-dom';

import {
  StatusLabel,
  Square,
  SquareButton,
  SingleFieldDialog,
  TSingleFieldDialogState,
  ConfirmDialog,
  TConfirmDialogState,
} from '@shared/components';

import { AccessDialog, TAccessDialogState, AccessType } from '@modules/access';
import { EAiAssistantOriginItemType, useRegisterAiAssistantOrigin } from '@modules/ai';

import { useGeneralActions } from '../hooks/useActions.hook';
import { useCreate, useFolder, useMarkFolderViewed } from '../api/folders.api';

import { IFolder } from '../types/folders.types';

import FoldersNavDrawer from '../components/nav/FoldersNavDrawer';
import ActivitySection from '@shared/pages/Home/ActivitySection';
import { useDocumentTitle } from '@shared/hooks';
import { buildDashboardHref } from '@modules/dashboard/helpers/dashboardScope';

type TPropsContent = {
  folder: IFolder;
};

const ProductContent = ({ folder }: TPropsContent) => {
  useRegisterAiAssistantOrigin({ item: folder._id, itemType: EAiAssistantOriginItemType.FOLDER });

  const [simpleDialogState, setSimpleDialogState] = useState<TSingleFieldDialogState<'input'> | undefined>(undefined);
  const [confirmDialogState, setConfirmDialogState] = useState<TConfirmDialogState | undefined>(undefined);
  const [accessDialogState, setAccessDialogState] = useState<TAccessDialogState | undefined>(undefined);
  const actions = useGeneralActions({
    setSimpleDialogState,
    setConfirmDialogState,
    setAccessDialogState,
    folder,
  });

  const create = useCreate();
  const handleCreate = () => {
    const dialogState: TSingleFieldDialogState<'input'> = {
      isOpen: true,
      defaultText: '',
      title: 'New jurisdiction',
      label: 'Name',
      buttonLabel: 'Create',
      onSubmit: (formData) => {
        create.mutate(
          { parentId: folder._id, name: formData.text },
          { onSuccess: () => setSimpleDialogState({ ...dialogState, isOpen: false }) }
        );
      },
    };
    setSimpleDialogState(dialogState);
  };

  const fullPath = [...folder.resolvedPath, { _id: folder._id, name: folder.name, accessRight: folder.accessRight }];
  const dashboardHref = useMemo(() => buildDashboardHref([folder._id]), [folder._id]);
  const topCards = useMemo(
    () =>
      folder.children
        .filter((item) => !item.isBlock && folder.snapshotsFolder?._id !== item._id)
        .map((item) => ({ item, cardStatus: folder.cardStatuses[item._id] })),
    [folder.cardStatuses, folder.children, folder.snapshotsFolder?._id]
  );

  return (
    <div className="flex flex-1">
      <FoldersNavDrawer fullPath={fullPath} selectedValue="overview" folder={folder} />
      <div className="grow h-full w-full relative">
        <div
          className="absolute top-0 left-0 w-full h-full overflow-y-auto pl-1 pr-8"
          style={{ scrollbarGutter: 'stable' }}
        >
          <div className="pt-4 pb-16">
            <div className="flex flex-col">
              <SingleFieldDialog state={simpleDialogState} setState={setSimpleDialogState} />
              <ConfirmDialog state={confirmDialogState} setState={setConfirmDialogState} />
              <AccessDialog state={accessDialogState} setState={setAccessDialogState} />

              <p className="flex space-x-4 mb-5">
                <Subtitle1>Product</Subtitle1>
              </p>
              <div className="flex flex-wrap gap-5 pb-12">
                {topCards.map(({ item, cardStatus }) => (
                  <Square key={item._id} item={item} isRoot={false} actions={actions} cardStatus={cardStatus} />
                ))}
                {folder.accessRight >= AccessType.EDIT && (
                  <SquareButton handleCreate={handleCreate}>New jurisdiction</SquareButton>
                )}
              </div>

              <ActivitySection
                recentItems={folder.recentItems}
                pendingApprovals={folder.pendingApprovals}
                dashboardHref={dashboardHref}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Product = () => {
  const { id } = useParams(); // Access the dynamic part of the route
  const { error, data } = useFolder(id as string); // id is always filled, otherwise NoPage.tsx is rendered
  const markFolderViewed = useMarkFolderViewed();

  useEffect(() => {
    markFolderViewed.mutate(id as string);
  }, [id]);

  useDocumentTitle(data ? (data as IFolder).name : 'Confideck');

  return (
    <StatusLabel
      data={data}
      loadingMessage="Loading product..."
      error={error}
      errorMessage="Loading product failed!"
      render={(resolvedData) => <ProductContent folder={resolvedData as IFolder} />}
    />
  );
};

export default Product;
